import { useMemo } from 'react';
import { Drone } from '../../types/drone';
import { FlightTrack } from '../../types/flightTrack';
import { useClusters } from '../../hooks/useClusters';
import { ClusterBadge } from './ClusterBadge';
import { DroneMarker } from './DroneMarker';
import { RouteLayer } from './RouteLayer';

interface FleetClusterLayerProps {
  drones: Drone[];
  /**
   * Flown paths to draw (#581). In practice this is the selected drone's track,
   * so one or none — the clustering below was written for the day several are
   * shown at once (#434) and costs nothing in the meantime.
   */
  tracks?: FlightTrack[];
}

/**
 * Mid-point of a flown path — where its track sits for clustering.
 *
 * The flown route is the whole path now: the `plannedRoute` this used to
 * concatenate was a field the API never sent (#581).
 */
const trackAnchor = (track: FlightTrack): { lat: number; lng: number } | null => {
  const path = track.traveledRoute;
  if (path.length === 0) {
    return null;
  }
  const [lat, lng] = path[Math.floor(path.length / 2)];
  return { lat, lng };
};

/**
 * The fleet map's drones and mission tracks, collapsed into counted badges
 * where they crowd each other (issue #434).
 *
 * Drones and tracks cluster separately and carry their own badge colour and
 * glyph, so a dense view still says *what* is there. A track's anchor is the
 * midpoint of its path; when several anchors crowd together their polylines
 * are replaced by one badge, and zooming in (or clicking the badge) brings the
 * individual routes back.
 */
export const FleetClusterLayer: React.FC<FleetClusterLayerProps> = ({
  drones,
  tracks = [],
}) => {
  // flatMap, not map: a drone with no reported position has no coordinates to
  // cluster by, and dereferencing it crashed the whole map (#508). Dropping it
  // here rather than upstream keeps the fleet list and the ownership counts
  // complete — this layer is the only place that needs a position to exist.
  const droneCandidates = useMemo(
    () =>
      drones.flatMap((drone) =>
        drone.position
          ? [
              {
                id: drone.id,
                lat: drone.position.latitude,
                lng: drone.position.longitude,
                item: drone,
              },
            ]
          : []
      ),
    [drones]
  );

  const trackCandidates = useMemo(
    () =>
      tracks.flatMap((track) => {
        const anchor = trackAnchor(track);
        return anchor
          ? [{ id: track.id, lat: anchor.lat, lng: anchor.lng, item: track }]
          : [];
      }),
    [tracks]
  );

  const droneClusters = useClusters<Drone>(droneCandidates);
  const trackClusters = useClusters<FlightTrack>(trackCandidates);

  return (
    <>
      {droneClusters.map((cluster) =>
        cluster.items.length === 1 ? (
          <DroneMarker key={cluster.items[0].id} drone={cluster.items[0]} />
        ) : (
          <ClusterBadge
            key={cluster.key}
            kind="drones"
            count={cluster.items.length}
            lat={cluster.lat}
            lng={cluster.lng}
            bounds={cluster.bounds}
          />
        )
      )}

      {trackClusters.map((cluster) =>
        cluster.items.length === 1 ? (
          <RouteLayer key={cluster.items[0].id} track={cluster.items[0]} />
        ) : (
          <ClusterBadge
            key={cluster.key}
            kind="tracks"
            count={cluster.items.length}
            lat={cluster.lat}
            lng={cluster.lng}
            bounds={cluster.bounds}
          />
        )
      )}
    </>
  );
};
